import { getDbSupabase } from './supabase';

export type PortalPresencePortal = 'crm' | 'it' | 'dem' | 'forms' | 'inventory' | 'accounts' | 'report' | 'purchasing' | 'design' | 'admin';

export type PortalPresenceRow = {
  auth_id: string;
  portal: PortalPresencePortal;
  email: string | null;
  display_name: string | null;
  last_seen_at: string;
};

export const PORTAL_PRESENCE_LABELS: Record<PortalPresencePortal, string> = {
  crm: 'Sales CRM',
  it: 'IT Management',
  dem: 'Data Entry Management',
  forms: 'Application Form',
  inventory: 'Inventory',
  accounts: 'Accounts',
  report: 'Report',
  purchasing: 'Purchasing',
  design: 'Design',
  admin: 'Settings',
};

export const PORTAL_PRESENCE_SHORT: Record<PortalPresencePortal, string> = {
  crm: 'CRM',
  it: 'IT',
  dem: 'DEM',
  forms: 'Forms',
  inventory: 'Inv',
  accounts: 'AR',
  report: 'Rpt',
  purchasing: 'Pur',
  design: 'Des',
  admin: 'Settings',
};

export const PORTAL_PRESENCE_STALE_MINUTES = 5;
export const PORTAL_PRESENCE_HEARTBEAT_MS = 15_000;
export const PORTAL_PRESENCE_RELATIVE_TICK_MS = 30_000;

export async function upsertPortalPresence(portal: PortalPresencePortal): Promise<void> {
  const { error } = await getDbSupabase('crm').rpc('upsert_portal_presence', {
    p_portal: portal,
  });
  if (error) throw error;
}

export async function logPortalOffline(): Promise<void> {
  const { error } = await getDbSupabase('crm').rpc('log_portal_offline');
  if (error) throw error;
}

export async function fetchPortalPresence(
  staleMinutes = PORTAL_PRESENCE_STALE_MINUTES,
): Promise<PortalPresenceRow[]> {
  const { data, error } = await getDbSupabase('crm').rpc('list_portal_presence', {
    p_stale_minutes: staleMinutes,
  });
  if (error) throw error;
  return (data ?? []) as PortalPresenceRow[];
}

export function isPortalPresenceFresh(
  lastSeenAt: string,
  staleMinutes = PORTAL_PRESENCE_STALE_MINUTES,
): boolean {
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return false;
  return Date.now() - seen <= staleMinutes * 60_000;
}

export function sortPortalPresenceRows(rows: PortalPresenceRow[]): PortalPresenceRow[] {
  return [...rows].sort((a, b) => {
    const byTime = new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
    if (byTime !== 0) return byTime;
    return (a.display_name ?? '').localeCompare(b.display_name ?? '');
  });
}

export function mergePortalPresenceRow(
  current: PortalPresenceRow[],
  row: PortalPresenceRow,
): PortalPresenceRow[] {
  const without = current.filter((item) => item.auth_id !== row.auth_id);
  if (!isPortalPresenceFresh(row.last_seen_at)) return without;
  return sortPortalPresenceRows([...without, row]);
}

export function removePortalPresenceRow(
  current: PortalPresenceRow[],
  authId: string,
): PortalPresenceRow[] {
  return current.filter((item) => item.auth_id !== authId);
}

export function pruneStalePortalPresenceRows(
  current: PortalPresenceRow[],
  staleMinutes = PORTAL_PRESENCE_STALE_MINUTES,
): PortalPresenceRow[] {
  return current.filter((item) => isPortalPresenceFresh(item.last_seen_at, staleMinutes));
}
